import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import type { ProductCostBreakdownRow } from "../types"

import DateChip from "@/components/DateChip"
import {
  CostBreakdownQuantityCell,
  CostBreakdownTotalLaidDownCostCell,
} from "@/screens/Procurement/CostBreakdown/components/CostBreakdownTable"
import { ProductVersionChip } from "@/screens/Products/components/ProductVersionChip"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const useProductCostBreakdownColumns = (): ColumnDef<ProductCostBreakdownRow>[] => {
  return useMemo<ColumnDef<ProductCostBreakdownRow>[]>(() => {
    return [
      {
        accessorKey: "grNumber",
        header: "GR #",
        cell: ({ row }) => <span className='font-bold text-primary'>{row.original.goodsReceipt.agrNumber ?? ""}</span>,
      },
      {
        accessorKey: "poNumber",
        header: "PO #",
        cell: ({ row }) => row.original.purchaseOrderLine.orderDetail?.orderId ?? "",
      },
      {
        accessorKey: "productVersion",
        header: "Product Version",
        cell: ({ row }) => {
          const product = row.original.purchaseOrderLine.product!
          const currentVersion = (product.versions ?? []).find((version) => version.id === product.versionId)

          if (!currentVersion) {
            return null
          }

          return <ProductVersionChip version={currentVersion} />
        },
      },
      {
        accessorKey: "warehouse",
        header: "Warehouse",
        cell: ({ row }) => formatWarehouseLabel(row.original.goodsReceipt.warehouseArea!),
      },
      {
        accessorKey: "quantity",
        header: "Qty Received",
        cell: ({ row }) => <CostBreakdownQuantityCell purchaseOrderLine={row.original.purchaseOrderLine} />,
      },
      {
        accessorKey: "totalLaidDownCost",
        header: "Total Laid-Down Cost per UoM",
        cell: ({ row }) => <CostBreakdownTotalLaidDownCostCell purchaseOrderLine={row.original.purchaseOrderLine} />,
      },
      {
        accessorKey: "checkedInOn",
        header: "Received Date",
        enableSorting: true,
        cell: ({ row }) => {
          const { checkedInOn } = row.original.goodsReceipt

          if (!checkedInOn) {
            return null
          }

          return <DateChip date={checkedInOn} className='bg-primary-200' />
        },
      },
      {
        accessorKey: "laidDownOn",
        header: "Laid Down Date",
        enableSorting: true,
        cell: ({ row }) => {
          const laidDownOn = row.original.purchaseOrderLine.costBreakdown?.laidDownOn

          if (!laidDownOn) {
            return null
          }

          return <DateChip date={laidDownOn} className='bg-primary-200' />
        },
      },
      {
        id: "expander",
        header: "",
        enableSorting: false,
        size: 70,
        cell: ({ row }) => (
          <Box className='flex items-center justify-end' onClick={(e) => e.stopPropagation()}>
            <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        ),
      },
    ]
  }, [])
}
